<template>
  <div class="bg-muted px-4 py-3 rounded-xl">
    <div
      v-if="showStockColumns"
      class="flex flex-col gap-3 md:grid md:grid-cols-[2fr_1fr_1fr_1fr_1fr_2.25rem] md:items-center md:gap-x-4"
    >
      <div class="flex items-center justify-between md:contents">
        <div class="flex items-center gap-1.5 flex-1 md:flex-none md:order-1">
          <div
            class="rounded-full w-8 h-8 flex items-center justify-center mr-2"
            :style="{
              background: background,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderColor: borderColor
            }"
          >
            <div
              v-if="!hasBackgroundImage"
              :style="{ color: getTextColor(item.color) }"
              class="text-sm font-normal uppercase"
            >
              {{ shortName }}
            </div>
          </div>
          <div class="flex-1 text-n-10 text-sm line-clamp-2">
            {{ item.name }}
          </div>
        </div>

        <div class="flex justify-center md:order-6">
          <l-switch
            :model-value="item.enabled && !isOutOfStock"
            size="medium"
            @update:model-value="handleSwitchClick"
          />
        </div>
      </div>

      <div class="grid grid-cols-3 gap-3 md:contents">
        <div class="col-span-1 md:order-2">
          <div
            v-if="hasStock && stockDisplayData"
            class="flex flex-col gap-1 items-center justify-between md:flex-row md:flex-wrap md:justify-center"
          >
            <div class="flex-1 text-n-0 text-sm text-center whitespace-nowrap">
              <div v-if="stockDisplayData.initialStock !== '-'">
                {{ stockDisplayData.initialStock }} {{ t('menu-availability.stock.units') }}
              </div>
              <div v-else>-</div>
            </div>
            <l-button class="h-fit flex-shrink-0" type="text" size="x-small" @click="editStock">
              {{ t('ctas.edit') }}
            </l-button>
          </div>
          <div v-else class="flex md:justify-center">
            <l-button
              class="h-fit px-1 py-1 text-xs md:px-2 md:py-1.5"
              type="secondary"
              size="x-small"
              @click="editStock"
            >
              {{ t('menu-availability.stock.define-stock') }}
            </l-button>
          </div>
        </div>

        <div class="md:text-center col-span-1 md:order-4">
          <div class="text-sm text-n-10">
            <span v-if="hasStock && stockDisplayData"
              >{{ stockDisplayData.remaining }} {{ t('menu-availability.stock.units') }}</span
            >
            <span v-else>-</span>
          </div>
        </div>

        <div class="md:text-center col-span-1 md:order-5">
          <div class="text-sm text-n-10">
            <span v-if="hasStock && stockDisplayData && stockDisplayData.needToRestock !== '-'"
              >{{ stockDisplayData.needToRestock }} {{ t('menu-availability.stock.units') }}</span
            >
            <span v-else>-</span>
          </div>
        </div>
      </div>

      <div class="text-center text-sm hidden md:block md:order-3">
        <span v-if="hasStock && stockDisplayData">
          {{ t(`menu-availability.stock.refillPolicy.${stockDisplayData.refillPolicy}.title`) }}
        </span>
        <span v-else>-</span>
      </div>
    </div>

    <div v-if="!showStockColumns" class="flex items-center justify-between">
      <div class="flex items-center gap-1.5">
        <div
          class="rounded-full w-8 h-8 flex items-center justify-center mr-2"
          :style="{
            background: background,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderColor: borderColor
          }"
        >
          <div
            v-if="!hasBackgroundImage"
            :style="{ color: getTextColor(item.color) }"
            class="text-sm font-normal uppercase"
          >
            {{ shortName }}
          </div>
        </div>
        <div class="flex-1 text-n-10 text-sm line-clamp-2">
          {{ item.name }}
        </div>
      </div>
      <l-switch
        :model-value="item.enabled && !isOutOfStock"
        size="medium"
        @update:model-value="handleSwitchClick"
      />
    </div>
  </div>

  <stock-limit-modal v-if="editingStock" :product-stock="props.item" @close="closeStock" />
  <disable-item-modal v-if="disablingItem" :item="item" @close="disablingItem = false" />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { darken } from '@last/core'
import { LButton, LSwitch } from '@last/core-ui/paprika'

import images from '@/images'
import { useConfigStore } from '@/store/config'
import { useStockStore } from '@/store/stock'
import type { StockItem } from '@/types'

import DisableItemModal from './DisableItemModal.vue'
import StockLimitModal from './StockLimitModal.vue'

type Props = {
  item: StockItem
  showStock?: boolean
}

const { t } = useI18n()

const props = defineProps<Props>()

const isOutOfStock = computed(() => props.item.stock === 0)
const hasStock = computed(() => props.item.stock !== undefined)
const showStockColumns = computed(() => props.item.type === 'product' && props.showStock)

const stockDisplayData = computed(() => {
  if (!showStockColumns.value) return null

  const refillValue = props.item.refillValue || 0
  const hasRefillValue = refillValue > 0

  return {
    initialStock: hasRefillValue ? refillValue : '-',
    needToRestock: hasRefillValue ? Math.max(refillValue - (props.item.stock ?? 0), 0) : '-',
    remaining: props.item.stock ?? 0,
    refillPolicy: props.item.refillPolicy || '-'
  }
})

const stockStore = useStockStore()
const configStore = useConfigStore()
const { config } = storeToRefs(configStore)

const shortName = computed(() => {
  if (props.item.shortName) {
    return props.item.shortName
  }
  if (!props.item.name) return ''
  const initials = props.item.name
    .split(/[.,/ -]/)
    .map(n => n[0])
    .join('')
    .slice(0, 2)
  if (initials.length < 2) {
    return props.item.name.slice(0, 2)
  } else {
    return initials
  }
})

const background = computed(() => {
  if (config.value.showProductImages && !!props.item.imageId) {
    const image = images.generateImageUrl(props.item.imageId, { width: 32 })
    return `url(${image})`
  } else {
    return props.item.color || '#363654'
  }
})

const hasBackgroundImage = computed(() => {
  return background.value.includes('url(', 0)
})

const borderColor = computed(() => {
  return darken(props.item.color!)
})

function getTextColor(hexcolor?: string) {
  if (!hexcolor) return 'white'
  const color = hexcolor.substr(1, 6)
  const r = parseInt(color.substr(0, 2), 16)
  const g = parseInt(color.substr(2, 2), 16)
  const b = parseInt(color.substr(4, 2), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 200 ? '#302F66' : 'white'
}

// OOS and disabled -> should enabled and open modal
// OOS and enabled -> should open modal
// enabled and not OOS -> should disabled
// disabled and not OOS -> should enabled
function handleSwitchClick() {
  const isEnabled = props.item.enabled
  if (isOutOfStock.value) editStock()
  else if (isEnabled) disableItem()
  if (!isEnabled) enableItem()
}

function enableItem() {
  stockStore.toggleItem(props.item, true)
}

const disablingItem = ref(false)
function disableItem() {
  if (props.showStock) disablingItem.value = true
  else stockStore.toggleItem(props.item, false)
}

const editingStock = ref(false)
function editStock() {
  editingStock.value = true
}

function closeStock() {
  editingStock.value = false
}

defineExpose({
  editingStock,
  handleSwitchClick
})
</script>
